<template>
  <div class="SubscriberContent">
    <div class="SubscriberContent-content">
      <ul class="SubscriberContent-list">
        <li
          v-for="item of allItems"
          :key="item.label"
          class="SubscriberContent-listItem"
        >
          <span class="SubscriberContent-listItemLabel">{{ item.label }}:</span>
          <span class="SubscriberContent-listItemValue">{{ item.value }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import type { SubscriberResponse } from '@cvo/domain/api/models/bff/Subscriber';
import { useDateFormat } from '@cvo/ui/composables/useDateFormat';
import type { ListItem } from '@cvo/ui/types/fields';

export interface SubscriberContentProps {
  subscriber: SubscriberResponse;
}

const props = defineProps<SubscriberContentProps>();

const emit = defineEmits<{
  retryPayment: [];
}>();

const { t } = useI18n();

const phone = computed(() => {
  const mobile = props.subscriber.person.phones?.find((currentPhone) => currentPhone.type === 'MOBILE');
  if(!mobile) return null;

  return `${mobile.prefix}${mobile.number}`;
});

const dateOfBirth = useDateFormat(computed(() => (props.subscriber.person.dateOfBirth ? new Date(props.subscriber.person.dateOfBirth) : undefined)), { dateFormat: 'short' });

const address = computed(() => {
  const homeAddress = props.subscriber.person.addresses?.find((currentAddress) => currentAddress.type === 'HOME');
  if (!homeAddress) return null;

  const { street, postalCode, city, state, country } = homeAddress;
  return t('ui.person.details.address.format', { street, city, state, postalCode, country });
});

const nationality = computed(() => props.subscriber.person.nationality);

const travelDoc = computed(() => props.subscriber.person.travelDocs?.[0]);
const expirationDate = computed(() => {
  if (!travelDoc.value) return null;
  return useDateFormat(new Date(travelDoc.value!.expirationDate), { dateFormat: 'short' }).value;
});
const loyaltyProgram = computed(() => props.subscriber.loyaltyPrograms?.[0]);

const documentItems = computed<ListItem[]>(() => (
  travelDoc.value && expirationDate.value
    ? [
        { label: t('ui.person.details.documentType.title'), value: t(`ui.form.fields.documentType.options.${travelDoc.value.type.toLowerCase()}`) },
        { label: t('ui.person.details.documentNumber.title'), value: travelDoc.value.number },
        ...(travelDoc.value.country ? [{ label: t('ui.person.details.documentCountry.title'), value: t(`countries.${travelDoc.value.country.toUpperCase()}`) }] : []),
        { label: t('ui.person.details.documentExpiration.title'), value: expirationDate.value },
      ]
    : []
));

const loyaltyItems = computed<ListItem[]>(() => (
  loyaltyProgram.value
    ? [
        { label: t('ui.subscriber.details.loyaltyProgram.name.title'), value: loyaltyProgram.value.level },
        { label: t('ui.subscriber.details.loyaltyProgram.number.title'), value: loyaltyProgram.value.program },
      ]
    : []
));

const allItems = computed<ListItem[]>(() => [
  { label: t('ui.person.details.email.title'), value: props.subscriber.person.email },
  { label: t('ui.person.details.birthDate.title'), value: dateOfBirth.value },
  { label: t('ui.person.details.gender.title'), value: t(`ui.person.details.gender.options.${props.subscriber.person.gender}`) },
  ...(nationality.value ? [{ label: t('ui.person.details.nationality.title'), value: t(`countries.${nationality.value.toUpperCase()}`) }] : []),
  ...(phone.value ? [{ label: t('ui.person.details.phone.title'), value: phone.value }] : []),
  ...(address.value ? [{ label: t('ui.person.details.address.title'), value: address.value }] : []),
  ...documentItems.value,
  ...loyaltyItems.value,
]);

const COLUMNS_COUNT = 2;
const gridRowCount = computed(() => Math.ceil(allItems.value.length / COLUMNS_COUNT));
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  $rowCount: v-bind(gridRowCount);

  .SubscriberContent {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-10;

    @include responsive.mobile {
      gap: vars.$dimensions-d-4;
    }

    &-content {
      display: flex;
      gap: vars.$dimensions-d-5;

      @include responsive.mobile {
        gap: vars.$dimensions-d-8;
      }
    }

    &-list {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-auto-flow: column;
      grid-template-rows: repeat($rowCount, auto);
      gap: vars.$dimensions-d-5 vars.$dimensions-d-12;
      padding: 0;
      margin: 0;
      list-style: none;
      flex: 1;
      align-items: flex-start;

      @include responsive.mobile {
        grid-template-columns: 1fr;
        grid-auto-flow: row;
        grid-template-rows: auto;
      }
    }

    &-listItem {
      display: flex;
      align-items: flex-start;
      column-gap: vars.$dimensions-d-3;
      row-gap: 0;
      flex-wrap: wrap;

      @include responsive.mobile {
        flex-direction: column;
        align-items: flex-start;
        gap: vars.$dimensions-d-1;
      }
    }

    &-listItemLabel {
      color: vars.$organisms-card-contentcard-details-label-color;
      white-space: nowrap;
      @include text.text-responsive('sm', 'md');

      // Wrapped so it is emitted after the mixin's media queries instead of being hoisted above them
      & {
        font-weight: vars.$typography-weight-strong;
      }
    }

    &-listItemValue {
      color: vars.$organisms-card-contentcard-details-text-color;
      flex: auto;
      @include text.text-responsive('sm', 'md');
    }
  }
}
</style>
